/**
 * 批量转移向导(4 步)— spec-school-student-admin.md §5.2 (P0-c)。
 * Step1 确认对象(可移除个别)→ Step2 目标校/班(人数/容量)→
 * Step3 预检报告(🔴阻断 / 🟡警告需逐项勾选确认 / ℹ️随迁清单)→
 * Step4 生效日期+原因 → 执行 → 逐人回执 + batch_id + CSV 导出。
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useExecuteTransfer,
  usePreflightTransfer,
  useSchoolClasses,
  useSchools,
  type PreflightResult,
  type SchoolStudentRow,
  type TransferResult,
} from "@/api/school";

export function TransferWizard({
  fromSchoolId,
  students,
  onClose,
  onDone,
}: {
  fromSchoolId: string;
  students: SchoolStudentRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const schools = useSchools();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState<SchoolStudentRow[]>(students);
  const [toSchool, setToSchool] = useState("");
  const [toClass, setToClass] = useState("");
  const targetClasses = useSchoolClasses(toSchool || undefined);
  const preflight = usePreflightTransfer();
  const execute = useExecuteTransfer();
  const [pf, setPf] = useState<PreflightResult | null>(null);
  const [acked, setAcked] = useState<Set<string>>(new Set());
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [result, setResult] = useState<TransferResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const targetSchools = (schools.data ?? []).filter((s) => s.id !== fromSchoolId);
  const classesByGrade = useMemo(() => {
    const m = new Map<string, typeof targetClasses.data>();
    for (const c of targetClasses.data ?? []) {
      const arr = m.get(c.grade) ?? [];
      arr.push(c);
      m.set(c.grade, arr as never);
    }
    return m;
  }, [targetClasses.data]);

  async function runPreflight() {
    setErr(null);
    try {
      const r = await preflight.mutateAsync({
        student_ids: selected.map((s) => s.id),
        to_school_id: toSchool,
        to_class_id: toClass,
      });
      setPf(r);
      setAcked(new Set());
      setStep(3);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function runExecute() {
    setErr(null);
    try {
      const r = await execute.mutateAsync({
        student_ids: selected.map((s) => s.id),
        to_school_id: toSchool,
        to_class_id: toClass,
        effective_date: effectiveDate,
        reason,
        acknowledged_warnings: Array.from(acked),
      });
      setResult(r);
      setStep(5);
    } catch (e) {
      const d = (e as { detail?: { error?: string } }).detail;
      setErr(d?.error ?? (e as Error).message);
    }
  }

  function exportCsv() {
    if (!result) return;
    const nameOf = (id: string) => selected.find((s) => s.id === id)?.name_zh ?? id;
    const lines = ["student,ok,integrity,error"].concat(
      result.results.map((r) => `${nameOf(r.student_id)},${r.ok},${r.integrity_check ?? ""},${r.message ?? ""}`),
    );
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `transfer_${result.batch_id}.csv`;
    a.click();
  }

  const allWarningsAcked = pf ? pf.warnings.every((w) => acked.has(w.code)) : false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[36rem] max-h-[85vh] overflow-y-auto rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">{t("transfer.title")}</h2>
        <p className="text-xs text-slate-400 mb-4">{t(`transfer.step${Math.min(step, 4)}Label`)}</p>

        {step === 1 && (
          <>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg mb-4">
              {selected.map((s) => (
                <li key={s.id} className="flex items-center px-3 py-2 text-sm">
                  <span className="text-slate-800">{s.name_zh}</span>
                  <span className="ml-2 text-xs text-slate-400">{s.class_name ?? "—"} · {s.grade}</span>
                  {s.status === "on_leave" && (
                    <span className="ml-2 text-xs px-1.5 rounded bg-amber-100 text-amber-700">{t("transfer.onLeaveTag")}</span>
                  )}
                  <button
                    onClick={() => setSelected((arr) => arr.filter((x) => x.id !== s.id))}
                    className="ml-auto text-xs text-rose-400 hover:text-rose-600"
                  >
                    {t("transfer.remove")}
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">{t("common.cancel")}</button>
              <button
                disabled={selected.length === 0}
                onClick={() => setStep(2)}
                className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm disabled:opacity-50"
              >
                {t("transfer.next")}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <label className="block text-sm mb-3">
              <span className="text-slate-600">{t("transfer.targetSchool")}</span>
              <select
                value={toSchool}
                onChange={(e) => { setToSchool(e.target.value); setToClass(""); }}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="">—</option>
                {targetSchools.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
            {toSchool && (
              <label className="block text-sm mb-4">
                <span className="text-slate-600">{t("transfer.targetClass")}</span>
                <select
                  value={toClass}
                  onChange={(e) => setToClass(e.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                >
                  <option value="">—</option>
                  {Array.from(classesByGrade.entries()).map(([grade, cls]) => (
                    <optgroup key={grade} label={grade}>
                      {(cls ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}({c.student_count}/{c.capacity})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            )}
            {err && <p className="text-xs text-rose-600 mb-2">{err}</p>}
            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">←</button>
              <button
                disabled={!toSchool || !toClass || preflight.isPending}
                onClick={runPreflight}
                className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm disabled:opacity-50"
              >
                {preflight.isPending ? "…" : t("transfer.runPreflight")}
              </button>
            </div>
          </>
        )}

        {step === 3 && pf && (
          <>
            {pf.blockers.map((b) => (
              <div key={b.code} className="mb-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                🔴 {b.message}
              </div>
            ))}
            {pf.warnings.map((w) => (
              <label key={w.code} className="mb-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <input
                  type="checkbox"
                  checked={acked.has(w.code)}
                  onChange={(e) => {
                    const next = new Set(acked);
                    if (e.target.checked) next.add(w.code); else next.delete(w.code);
                    setAcked(next);
                  }}
                  className="mt-0.5"
                />
                <span>🟡 {w.message} — {t("transfer.ackLabel")}</span>
              </label>
            ))}
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              ℹ️ {t("transfer.infoLine", {
                a: pf.info.records_following.assignments,
                e: pf.info.records_following.exams,
                r: pf.info.records_following.reports,
                i: pf.info.records_following.itarget,
              })}
              {(pf.info.in_flight.assignments ?? 0) + (pf.info.in_flight.exams ?? 0) > 0 && (
                <div className="mt-1">
                  {t("transfer.inflightLine", { n: (pf.info.in_flight.assignments ?? 0) + (pf.info.in_flight.exams ?? 0) })}
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">←</button>
              <button
                disabled={!pf.can_proceed || !allWarningsAcked}
                onClick={() => setStep(4)}
                className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm disabled:opacity-50"
              >
                {t("transfer.next")}
              </button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <label className="block text-sm mb-3">
              <span className="text-slate-600">{t("transfer.effectiveDate")} *</span>
              <input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="mt-1 rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-sm mb-4">
              <span className="text-slate-600">{t("transfer.reason")} *</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("transfer.reasonPlaceholder")}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </label>
            {err && <p className="text-xs text-rose-600 mb-2">{err}</p>}
            <div className="flex justify-between">
              <button onClick={() => setStep(3)} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">←</button>
              <button
                disabled={!reason.trim() || !effectiveDate || execute.isPending}
                onClick={runExecute}
                className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm disabled:opacity-50"
              >
                {execute.isPending ? "…" : t("transfer.execute", { n: selected.length })}
              </button>
            </div>
          </>
        )}

        {step === 5 && result && (
          <>
            <p className="mb-3 text-sm text-slate-700">
              {t("transfer.resultLine", { ok: result.succeeded, fail: result.failed })}
              <span className="ml-2 font-mono text-xs text-slate-400">batch {result.batch_id.slice(0, 8)}</span>
            </p>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg mb-4 max-h-60 overflow-y-auto">
              {result.results.map((r) => (
                <li key={r.student_id} className="flex items-center px-3 py-2 text-sm">
                  <span>{selected.find((s) => s.id === r.student_id)?.name_zh ?? r.student_id}</span>
                  {r.ok ? (
                    <span className="ml-auto text-xs text-emerald-600">✓ {r.integrity_check}</span>
                  ) : (
                    <span className="ml-auto text-xs text-rose-600">✗ {r.message ?? r.error}</span>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-between">
              <button onClick={exportCsv} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
                {t("transfer.exportCsv")}
              </button>
              <button onClick={() => { onDone(); onClose(); }} className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm">
                {t("transfer.done")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
